import { InjectionToken } from '@angular/core';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  serverTimestamp,
  Timestamp,
  type CollectionReference,
  type DocumentData,
  type Query,
  type QuerySnapshot,
  type DocumentReference,
  type QueryConstraint,
  type Firestore,
  type FieldValue,
} from 'firebase/firestore';

/**
 * Firestore のクエリ・書き込み操作関数をまとめた型定義。
 *
 * バンドラ環境（Angular テストビルダー等）では `firebase/firestore` の
 * モジュール解決パスがテストファイルとサービスで分岐し、`vi.mock()` で
 * 設定したモックが反映されないケースがある。
 *
 * この InjectionToken を介して操作関数を注入することで、テスト時に
 * 容易にモック差し替えが可能となり、バンドラのモジュール解決に依存しない
 * テスト可能な設計を実現する。
 */
export interface FirestoreOperations {
  collection: (firestore: Firestore, path: string) => CollectionReference<DocumentData>;
  query: (reference: CollectionReference<DocumentData>, ...queryConstraints: QueryConstraint[]) => Query<DocumentData>;
  where: (fieldPath: string, opStr: any, value: unknown) => QueryConstraint;
  orderBy: (fieldPath: string, directionStr?: 'asc' | 'desc') => QueryConstraint;
  getDocs: (query: Query<DocumentData>) => Promise<QuerySnapshot<DocumentData>>;
  addDoc: (reference: CollectionReference<DocumentData>, data: DocumentData) => Promise<DocumentReference<DocumentData>>;
  serverTimestamp: () => FieldValue;
  Timestamp: typeof Timestamp;
}

export const FIRESTORE_OPS_TOKEN = new InjectionToken<FirestoreOperations>(
  'Firestore Operations',
  {
    providedIn: 'root',
    factory: () => ({
      collection,
      query,
      where,
      orderBy,
      getDocs,
      addDoc,
      serverTimestamp,
      Timestamp,
    }),
  }
);
